<template>
    <Card>
        <template v-slot:icon>
            <img :src="integration.icon_url" :alt="integration.name" class="h-1em">
        </template>
        <template v-slot:title>
            {{ __('integrations.mc.lia_statuses') }}
        </template>
        <template v-slot:description>
            {{ __('general.last_update') }} {{ lastUpdateString || initialLastUpdateTimestamp }}
        </template>
        <template v-slot:toolbar>
            <div class="mr-2 d-flex flex-row gap-2 align-items-center">
                <div class="text-muted">
                    <span>{{ __('actions.filter_by_thing', {thing: __('general.account_issues')}) }}:</span>
                </div>
                <div>
                    <select class="form-control"
                            v-model="accountIssueFilter"
                            :disabled="presentAccountIssueTypes.length === 0 || statuses.length === 0"
                    >
                        <option value="" selected>
                            {{ __('general.no_filter') }}
                        </option>
                        <option v-for="issueType in presentAccountIssueTypes"
                                :value="issueType"
                                v-text="issueType"
                        />
                    </select>
                </div>
            </div>
            <div>
                <SearchInput v-model="searchString"
                             :disabled="statuses.length === 0"
                />
            </div>
        </template>

        <div class="table-responsive">
            <table class="table table-hover table-vertical-center">
                <thead>
                <tr>
                    <th>
                        {{ __('general.store') }}
                    </th>
                    <th class="text-center w-50px">
                        {{ __('general.issues') }}
                    </th>
                    <th class="text-center w-200px">
                        YBD-{{ __('general.provider') }}
                    </th>
                    <th class="text-center">
                        {{ __('general.miscellaneous') }}
                    </th>
                    <th class="text-center min-w-20px">
                        {{ __('integrations.mc.inventory_active') }}
                    </th>
                    <th class="text-center">
                        {{ __('integrations.mc.lfs_type') }}
                    </th>
                    <th class="text-center min-w-100px max-w-200px">
                        {{ __(`integrations.mc.destinations.${MerchantCenterProductDestination.FreeLocalListings}`) }}
                    </th>
                    <th class="text-center min-w-100px max-w-200px">
                        {{ __(`integrations.mc.destinations.${MerchantCenterProductDestination.LocalInventoryAds}`) }}
                    </th>
                    <th class="text-center min-w-100px max-w-200px">
                        {{ __(`integrations.mc.destinations.${MerchantCenterProductDestination.FreeListings}`) }}
                    </th>
                    <th class="text-center min-w-100px max-w-200px">
                        {{ __(`integrations.mc.destinations.${MerchantCenterProductDestination.ShoppingAds}`) }}
                    </th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="liaStatus in filteredLiaStatuses" :key="liaStatus.store_uuid">
                    <td class="py-5" :key="[liaStatus.store_uuid, liaStatus.updated_at, 'store_details'].join()">
                        <div v-if="liaStatus.merchant_id">
                            <a class="text-inherit font-weight-bold"
                               :href="`https://merchants.google.com/mc/overview?a=${liaStatus.merchant_id}`"
                               target="_new"
                            >
                                {{ liaStatus.merchant_id }}
                            </a>
                        </div>
                        <div v-if="liaStatus.store_code" class="text-muted font-size-sm text-wrap">
                            {{ liaStatus.store_code }}
                        </div>
                        <div class="d-flex flex-row align-items-center mt-2">
                            <div v-if="liaStatus.store" style="height: 1.025em; margin: 3px 0 5px -3px;">
                                <CountryFlag :country='liaStatus.store.attributes.address.country'
                                             size='small'
                                             rounded
                                             class="d-inline-block"
                                />
                            </div>
                            <div style="height: 1.025em;">
                                <div class="text-muted text-nowrap font-weight-bold font-size-xs">
                                    {{ liaStatus.store?.attributes?.name || 'Unknown store' }}
                                </div>
                            </div>
                        </div>
                        <div class="text-muted text-nowrap font-size-xs">
                            {{ liaStatus.store_uuid }}
                        </div>
                    </td>
                    <td class="text-center">
                        <MerchantCenterAccountStatusIcon :account-issues="liaStatus.account_issues"/>
                    </td>
                    <td class="text-center" :key="[liaStatus.store_uuid, liaStatus.updated_at, 'is_provider'].join()">
                        <i v-if="liaStatus.ybd_is_provider" class="fas fa-check-circle text-success"/>
                        <i v-else class="fas fa-times-circle text-danger"/>
                    </td>
                    <td class="text-nowrap" :key="[liaStatus.store_uuid, liaStatus.updated_at, 'summary'].join()">
                        <table class="table table-sm table-borderless table-vertical-center my-0">
                            <tbody>
                            <tr>
                                <td class="text-center py-0">
                                    <i v-if="liaStatus.website_claimed" class="fas fa-check text-success"/>
                                    <i v-else class="fas fa-times text-danger"/>
                                </td>
                                <td class="max-w-150px py-0 text-truncate">
                                    {{ __('general.thing.claimed', {thing: __('general.website')}) }}
                                </td>
                            </tr>
                            <tr>
                                <td class="text-center py-0">
                                    <a v-if="liaStatus.imprint_url"
                                       :href="liaStatus.imprint_url"
                                       target="_new"
                                    >
                                        <i v-if="isYoubuydaUrl(liaStatus.imprint_url)"
                                           class="fas fa-check text-inherit"/>
                                        <i v-else class="fas fa-times-circle text-danger"/>
                                    </a>
                                    <i v-else class="fas fa-times-circle text-danger"/>
                                </td>
                                <td class="max-w-150px py-0 text-truncate">
                                    YBD-{{ __('general.imprint') }}
                                </td>
                            </tr>
                            <tr>
                                <td class="text-center py-0">
                                    <i v-if="liaStatus.inventory_verified" class="fas fa-check text-success"
                                       :title="`Contact: ${liaStatus.inventory_contact}`"/>
                                    <i v-else class="fas fa-times text-muted"
                                       :title="`Contact: ${liaStatus.inventory_contact}`"/>
                                </td>
                                <td class="max-w-150px py-0 text-truncate">
                                    {{ __('integrations.mc.inventory_contact_verified') }}
                                </td>
                            </tr>
                            </tbody>
                        </table>
                    </td>
                    <td class="text-center" :key="[liaStatus.store_uuid, liaStatus.updated_at, 'is_active'].join()">
                        <i v-if="liaStatus.inventory_active" class="fas fa-check text-success"/>
                        <i v-else class="fas fa-times-circle text-danger"/>
                    </td>
                    <td class="text-center font-size-sm font-weight-bold"
                        :key="[liaStatus.store_uuid, liaStatus.updated_at, 'lsf_Type'].join()">
                        {{ readableLsfType(liaStatus.lsf_type) }}
                    </td>
                    <td class="text-center px-lg-5 px-2">
                        <MerchantCenterAddonItemStatusBox v-if="liaStatus.addon_local_surfaces"
                                                          :title="__(`integrations.mc.destinations.${MerchantCenterProductDestination.FreeLocalListings}`)"
                                                          :addon-status="liaStatus.addon_local_surfaces"
                                                          :key="[liaStatus.store_uuid, liaStatus.updated_at, 'addon_local_surfaces'].join()"
                        />
                        <span v-else class="text-muted">&dash;</span>
                    </td>
                    <td class="text-center px-lg-5 px-2">
                        <MerchantCenterAddonItemStatusBox v-if="liaStatus.addon_local_shopping"
                                                          :title="__(`integrations.mc.destinations.${MerchantCenterProductDestination.LocalInventoryAds}`)"
                                                          :addon-status="liaStatus.addon_local_shopping"
                                                          :key="[liaStatus.store_uuid, liaStatus.updated_at, 'addon_local_shopping'].join()"
                        />
                        <span v-else class="text-muted">&dash;</span>
                    </td>
                    <td class="text-center px-lg-5 px-2">
                        <MerchantCenterAddonItemStatusBox v-if="liaStatus.addon_online_surfaces"
                                                          :title="__(`integrations.mc.destinations.${MerchantCenterProductDestination.FreeListings}`)"
                                                          :addon-status="liaStatus.addon_online_surfaces"
                                                          :key="[liaStatus.store_uuid, liaStatus.updated_at, 'addon_online_surfaces'].join()"
                        />
                        <span v-else class="text-muted">&dash;</span>
                    </td>
                    <td class="text-center px-lg-5 px-2">
                        <MerchantCenterAddonItemStatusBox v-if="liaStatus.addon_online_shopping"
                                                          :title="__(`integrations.mc.destinations.${MerchantCenterProductDestination.ShoppingAds}`)"
                                                          :addon-status="liaStatus.addon_online_shopping"
                                                          :key="[liaStatus.store_uuid, liaStatus.updated_at, 'addon_online_shopping'].join()"
                        />
                        <span v-else class="text-muted">&dash;</span>
                    </td>
                </tr>
                </tbody>
            </table>
        </div>
    </Card>
</template>

<script>
import Card from "@components/Cards/Card.vue";
import CountryFlag from "vue-country-flag";
import Store from "@components/Store/Store.vue";
import StoreAddress from "@components/Store/StoreAddress.vue";
import SearchInput from "@components/Form/SearchInput.vue";
import MerchantCenterAddonItemStatusBox
    from "@components/Integrations/MerchantCenter/MerchantCenterAddonItemStatusBox.vue";
import Tooltip from "@components/Tooltip.vue";
import MerchantCenterAccountStatusIcon
    from "@components/Integrations/MerchantCenter/MerchantCenterAccountStatusIcon.vue";
import {MerchantCenterProductDestination} from "@generated/merchantCenterProductDestination";
import {getQueryParameter, rewriteUrlNew} from "@composables/rewriteUrlNew";

export default {
    components: {
        MerchantCenterAccountStatusIcon,
        Tooltip, MerchantCenterAddonItemStatusBox, SearchInput, StoreAddress, Store, Card, CountryFlag
    },

    props: {
        liaStatuses: Array,
        integration: Object,
    },

    data() {
        return {
            statuses: this.liaStatuses,
            lastUpdateString: '',
            searchString: getQueryParameter('search', ''),
            accountIssueFilter: getQueryParameter('account_issue', ''),
        }
    },

    mounted() {
        this.startListeningForUpdates();
    },

    methods: {
        isYoubuydaUrl(url) {
            return url.includes('//youbuyda.')
        },

        readableLsfType(type) {
            switch (type) {
                case 'ghlsf':
                    return 'Google Hosted';
                case 'mhlsfFull':
                    return 'YBD Shop';
                default:
                    return type;
            }
        },

        startListeningForUpdates() {
            window.Echo.private('lia-statuses').listen('.updated', this.handleStatusUpdate);
        },

        handleStatusUpdate(newLiaStatus) {
            let index = this.statuses.findIndex(liaStatus => liaStatus.store_uuid === newLiaStatus.store_uuid);

            logDev('Updating LIA status.', {
                new_status: newLiaStatus,
                at_index: index
            })

            if (index === -1) {
                this.statuses.push(newLiaStatus);
            } else {
                this.$set(this.statuses, index, newLiaStatus);
            }

            this.lastUpdateString = this.$localDateTime(newLiaStatus.updated_at);
        }
    },

    computed: {
        MerchantCenterProductDestination() {
            return MerchantCenterProductDestination
        },

        filteredLiaStatuses() {
            if (this.accountIssueFilter === '' && this.searchString.trim().length === 0) {
                return this.statuses;
            }

            return this.statuses.filter(liaStatus => {
                if (this.accountIssueFilter !== '') {
                    let matchesFilter = false;

                    for (let accountIssue of liaStatus.account_issues) {
                        if (accountIssue.id === this.accountIssueFilter) {
                            matchesFilter = true;
                        }
                    }

                    if (!matchesFilter) {
                        return false;
                    }
                }

                if (liaStatus.store) {
                    if (liaStatus.store.attributes.name.toLowerCase().includes(this.searchString.toLowerCase())) {
                        return true;
                    }
                }

                if (liaStatus.merchant_id?.includes(this.searchString)) {
                    return true;
                }

                if (liaStatus.store_code?.includes(this.searchString)) {
                    return true;
                }

                return liaStatus.store_uuid === this.searchString;
            });
        },

        presentAccountIssueTypes() {
            let uniqueAccountIssueTypes = [];

            for (let liaStatus of this.statuses) {
                for (let accountIssue of liaStatus.account_issues) {
                    uniqueAccountIssueTypes.pushUnique(accountIssue.id);
                }
            }

            return uniqueAccountIssueTypes;
        },

        initialLastUpdateTimestamp() {
            if (this.statuses.length === 0) {
                return __('general.unknown');
            }

            return this.$localDateTime(this.statuses[0].updated_at);
        }
    },

    watch: {
        searchString(newValue, oldValue) {
            rewriteUrlNew('search', newValue)
        },
        accountIssueFilter(newValue, oldValue) {
            rewriteUrlNew('account_issue', newValue)
        }
    }
}
</script>
